/*
 * JD Import page — POC visual locked, now wired to the real John Deere
 * connection via useJohnDeere / useJohnDeereOrganizations / useJohnDeereTree /
 * useJohnDeereImport.
 *
 * Data flow:
 *   1. Gate on isConnected (user must connect JD from the Account page first)
 *   2. Fetch JD organizations, auto-pick first enabled org (persisted to
 *      localStorage under "jd_import_org_id")
 *   3. Fetch the org's full client → farm → field → boundary tree
 *   4. In parallel, query SVM's client/parcel/site/boundary lists to build
 *      the "already imported" lookup by parsing "(JD-xxxxxxxx)" tags from
 *      entity names
 *   5. User checks nodes; checking a child auto-checks its ancestors;
 *      unchecking a parent cascades-remove all descendants
 *   6. Review modal shows summary; Confirm runs useJohnDeereImport.importSelected
 *   7. After a successful import, re-fetch the imported lookup so the UI
 *      refreshes the imported-badge state
 *
 * Visual is byte-faithful to docs/prototypes/jd-import-prototype.html.
 */
import type { BoundaryPartRequestBoundaryType } from 'generatedTypes/flight/models';
import { AttributionControl, LngLatBounds, Map as MapboxMap } from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JohnDeereBoundary as JohnDeereTreeBoundary } from 'types/johnDeere';

import useImports from 'hooks/useImports';
import { JOHN_DEERE_SOURCE } from 'hooks/useImports/constants';
import useJohnDeere from 'hooks/useJohnDeere';
import useJohnDeereImport from 'hooks/useJohnDeereImport';
import useJohnDeereOrganizations from 'hooks/useJohnDeereOrganizations';
import useJohnDeereTree from 'hooks/useJohnDeereTree';
import type {
    JohnDeereTreeClient,
    JohnDeereTreeFarm,
    JohnDeereTreeField
} from 'hooks/useJohnDeereTree/types';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

type NodeType = 'client' | 'farm' | 'field' | 'boundary';

type TypeColor = { fill: string; stroke: string; dash: boolean };

const TYPE_LABEL: Record<BoundaryPartRequestBoundaryType, string> = {
    APPLY: 'Apply',
    NO_APPLY: 'No-Apply',
    NO_ENTRY: 'No-Entry'
};

const TYPE_COLOR: Record<BoundaryPartRequestBoundaryType, TypeColor> = {
    APPLY: { dash: false, fill: '#7dc356', stroke: '#7dc356' },
    NO_APPLY: { dash: false, fill: '#e8a030', stroke: '#e8a030' },
    NO_ENTRY: { dash: true, fill: '#bd1536', stroke: '#bd1536' }
};

const BOUNDARY_TYPE_OPTIONS: BoundaryPartRequestBoundaryType[] = ['APPLY', 'NO_APPLY', 'NO_ENTRY'];

const DEFAULT_BOUNDARY_TYPE: BoundaryPartRequestBoundaryType = 'APPLY';

const MAP_STYLE = 'mapbox://styles/mapbox/satellite-streets-v12';

const LOCALSTORAGE_ORG_KEY = 'jd_import_org_id';

const EARTH_RADIUS_METERS = 6378137;

const SQM_PER_HECTARE = 10000;

const MIN_RING_POINTS = 3;

const FIT_BOUNDS_PADDING_PX = 40;

const DEFAULT_MAP_CENTER: [number, number] = [-97.7855, 40.704];

const DEFAULT_MAP_ZOOM = 14;

const toRadians = (deg: number): number => (deg * Math.PI) / 180;

const ringAreaSqm = (points: { lat: number; lon: number }[]): number => {
    if (points.length < MIN_RING_POINTS) {
        return 0;
    }
    let total = 0;

    for (let index = 0; index < points.length; index += 1) {
        const current = points[index];
        const next = points[(index + 1) % points.length];

        total +=
            toRadians(next.lon - current.lon) *
            (2 + Math.sin(toRadians(current.lat)) + Math.sin(toRadians(next.lat)));
    }

    return Math.abs((total * EARTH_RADIUS_METERS * EARTH_RADIUS_METERS) / 2);
};

const computeBoundaryAreaHa = (boundary: JohnDeereTreeBoundary): number => {
    if (!boundary.multipolygons?.length) {
        return 0;
    }
    let sqm = 0;

    for (const multipolygon of boundary.multipolygons) {
        for (const ring of multipolygon.rings) {
            const area = ringAreaSqm(ring.points);

            sqm += ring.type === 'exterior' ? area : -area;
        }
    }

    return Math.max(0, sqm / SQM_PER_HECTARE);
};

const computeFieldAreaHa = (field: JohnDeereTreeField): number =>
    (field.boundaries ?? []).reduce(
        (total, boundary) => total + computeBoundaryAreaHa(boundary),
        0
    );

const boundaryToGeoJsonCoords = (boundary: JohnDeereTreeBoundary): number[][][] | null => {
    const firstRing = boundary.multipolygons?.[0]?.rings?.find(ring => ring.type === 'exterior');

    if (!firstRing) {
        return null;
    }
    const exterior = firstRing.points.map(p => [p.lon, p.lat]);
    const interiors = (boundary.multipolygons?.[0]?.rings ?? [])
        .filter(ring => ring.type === 'interior')
        .map(ring => ring.points.map(p => [p.lon, p.lat]));

    return [exterior, ...interiors];
};

const findAncestorIds = (id: string, nodeType: NodeType, tree: JohnDeereTreeClient[]): string[] => {
    if (nodeType === 'client') {
        return [];
    }

    for (const client of tree) {
        if (nodeType === 'farm' && client.farms?.some(f => f.id === id)) {
            return [client.id];
        }

        for (const farm of client.farms ?? []) {
            if (nodeType === 'field' && farm.fields?.some(f => f.id === id)) {
                return [client.id, farm.id];
            }

            for (const field of farm.fields ?? []) {
                if (nodeType === 'boundary' && field.boundaries?.some(b => b.id === id)) {
                    return [client.id, farm.id, field.id];
                }
            }
        }
    }

    return [];
};

const findDescendantIds = (
    id: string,
    nodeType: NodeType,
    tree: JohnDeereTreeClient[]
): string[] => {
    const descendants: string[] = [];

    const collectField = (field: JohnDeereTreeField) => {
        descendants.push(field.id);
        (field.boundaries ?? []).forEach(b => descendants.push(b.id));
    };

    const collectFarm = (farm: JohnDeereTreeFarm) => {
        descendants.push(farm.id);
        (farm.fields ?? []).forEach(collectField);
    };

    for (const client of tree) {
        if (nodeType === 'client' && client.id === id) {
            (client.farms ?? []).forEach(collectFarm);

            return descendants;
        }

        for (const farm of client.farms ?? []) {
            if (nodeType === 'farm' && farm.id === id) {
                (farm.fields ?? []).forEach(collectField);

                return descendants;
            }

            for (const field of farm.fields ?? []) {
                if (nodeType === 'field' && field.id === id) {
                    (field.boundaries ?? []).forEach(b => descendants.push(b.id));

                    return descendants;
                }
            }
        }
    }

    return descendants;
};

const isClientOrgEnabled = (client: { links: { rel: string }[] }): boolean =>
    !client.links.some(link => link.rel === 'connections');

const useBoundaryMap = (boundaries: JohnDeereTreeBoundary[]) => {
    // Callback ref fires whenever the DOM node attaches or detaches, so the
    // Mapbox init effect re-runs if the container appears on a later render
    // (e.g. after `isConnected` flips and the main layout mounts).
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const containerRef = useCallback((node: HTMLDivElement | null) => {
        setContainer(node);
    }, []);
    const mapRef = useRef<MapboxMap | null>(null);

    useEffect(() => {
        if (!container) {
            return;
        }
        const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string | undefined;

        if (!token) {
            console.error('VITE_MAPBOX_ACCESS_TOKEN missing — map will not render');

            return;
        }

        const map = new MapboxMap({
            accessToken: token,
            attributionControl: false,
            center: DEFAULT_MAP_CENTER,
            container,
            style: MAP_STYLE,
            zoom: DEFAULT_MAP_ZOOM
        });

        map.addControl(new AttributionControl({ compact: true }), 'bottom-left');
        map.dragRotate.disable();
        mapRef.current = map;

        return () => {
            mapRef.current = null;
            map.remove();
        };
    }, [container]);

    useEffect(() => {
        const map = mapRef.current;

        if (!map) {
            return;
        }

        const applyLayers = () => {
            const styleLayers = map.getStyle()?.layers ?? [];

            styleLayers.filter(l => l.id.startsWith('jd-b-')).forEach(l => map.removeLayer(l.id));
            const styleSources = Object.keys(map.getStyle()?.sources ?? {}).filter(id =>
                id.startsWith('jd-b-')
            );

            styleSources.forEach(id => map.removeSource(id));

            const bounds = new LngLatBounds();
            let added = 0;

            boundaries.forEach(boundary => {
                const coords = boundaryToGeoJsonCoords(boundary);

                if (!coords) {
                    return;
                }
                const color = TYPE_COLOR.APPLY;
                const sourceId = `jd-b-${boundary.id}`;

                map.addSource(sourceId, {
                    data: {
                        geometry: { coordinates: coords, type: 'Polygon' },
                        properties: {},
                        type: 'Feature'
                    },
                    type: 'geojson'
                });

                map.addLayer({
                    id: `${sourceId}-fill`,
                    paint: { 'fill-color': color.fill, 'fill-opacity': 0.3 },
                    source: sourceId,
                    type: 'fill'
                });

                map.addLayer({
                    id: `${sourceId}-line`,
                    paint: {
                        'line-color': color.stroke,
                        'line-width': 2,
                        ...(color.dash ? { 'line-dasharray': [2, 2] } : {})
                    },
                    source: sourceId,
                    type: 'line'
                });
                coords[0].forEach(pair => bounds.extend([pair[0], pair[1]]));
                added += 1;
            });

            if (added > 0 && !bounds.isEmpty()) {
                map.fitBounds(bounds, { duration: 0, padding: FIT_BOUNDS_PADDING_PX });
            }
        };

        if (map.isStyleLoaded()) {
            applyLayers();
        } else {
            map.once('load', applyLayers);
        }
    }, [boundaries]);

    return containerRef;
};

const ImportPage: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const { isConnected } = useJohnDeere();
    const { fetchImports } = useImports();
    const { fetch: fetchOrgs, organizations } = useJohnDeereOrganizations();
    const { fetch: fetchTree, isLoading: isTreeLoading, tree } = useJohnDeereTree();
    const { importSelected, isImporting } = useJohnDeereImport();

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [openIds, setOpenIds] = useState<Set<string>>(new Set());
    const [boundaryTypes, setBoundaryTypes] = useState<
        Record<string, BoundaryPartRequestBoundaryType>
    >({});
    const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
    const [isReviewOpen, setIsReviewOpen] = useState(false);
    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() =>
        window.localStorage.getItem(LOCALSTORAGE_ORG_KEY)
    );
    const [importedIds, setImportedIds] = useState<Set<string>>(new Set());
    const [jdToSvmIdMap, setJdToSvmIdMap] = useState<Record<string, string>>({});
    const [lastSyncedAt, setLastSyncedAt] = useState<string>('');
    const hasInitialOrgPick = useRef(false);

    const refreshImportedLookup = useCallback(async () => {
        try {
            const mappings = await fetchImports({
                externalSource: JOHN_DEERE_SOURCE
            });

            if (mappings) {
                setImportedIds(new Set(mappings.map(mapping => mapping.externalId)));

                setJdToSvmIdMap(
                    Object.fromEntries(
                        mappings.map(mapping => [mapping.externalId, mapping.svmEntityId])
                    )
                );
            }
        } catch (error) {
            console.error('Failed to refresh imported lookup:', error);
        }
    }, [fetchImports]);

    // On mount: fetch orgs + SVM imported lookup
    useEffect(() => {
        if (!isConnected) {
            return;
        }
        fetchOrgs();
        refreshImportedLookup();
    }, [isConnected, fetchOrgs, refreshImportedLookup]);

    // Auto-pick first enabled org once (if none selected)
    useEffect(() => {
        if (hasInitialOrgPick.current || selectedOrgId || organizations.length === 0) {
            return;
        }
        const firstEnabled = organizations.find(isClientOrgEnabled);

        if (firstEnabled) {
            hasInitialOrgPick.current = true;
            setSelectedOrgId(firstEnabled.id);
        }
    }, [organizations, selectedOrgId]);

    // When selectedOrgId changes: persist, fetch tree, reset selection
    useEffect(() => {
        if (!isConnected || !selectedOrgId) {
            return;
        }
        window.localStorage.setItem(LOCALSTORAGE_ORG_KEY, selectedOrgId);
        fetchTree({ organizationId: selectedOrgId });
        setSelectedIds(new Set());
        setOpenIds(new Set());
        setBoundaryTypes({});
        setActiveFieldId(null);
        setLastSyncedAt(new Date().toISOString().slice(0, 16).replace('T', ' ') + ' UTC');
    }, [isConnected, selectedOrgId, fetchTree]);

    // When the tree loads, auto-expand every grower + farm so the user sees
    // all associations immediately without clicking into each row. Fields
    // stay collapsed — click the Show button on a field to reveal boundaries.
    useEffect(() => {
        if (tree.length === 0) {
            return;
        }

        setOpenIds(previous => {
            const next = new Set(previous);

            for (const client of tree) {
                next.add(client.id);

                for (const farm of client.farms ?? []) {
                    next.add(farm.id);
                }
            }

            return next;
        });
    }, [tree]);

    const selectedOrg = organizations.find(org => org.id === selectedOrgId) ?? null;

    const toggleSelected = useCallback(
        (id: string, nodeType: NodeType) => {
            setSelectedIds(previous => {
                const next = new Set(previous);

                if (next.has(id)) {
                    next.delete(id);

                    findDescendantIds(id, nodeType, tree).forEach(descId => next.delete(descId));
                } else {
                    next.add(id);

                    findAncestorIds(id, nodeType, tree).forEach(ancestorId => next.add(ancestorId));
                    findDescendantIds(id, nodeType, tree).forEach(descId => next.add(descId));
                }

                return next;
            });
        },
        [tree]
    );

    const toggleOpen = useCallback((id: string) => {
        setOpenIds(previous => {
            const next = new Set(previous);

            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });
    }, []);

    const toggleFieldOpen = useCallback(
        (fieldId: string) => {
            toggleOpen(fieldId);
            setActiveFieldId(fieldId);
        },
        [toggleOpen]
    );

    const setType = useCallback((id: string, type: BoundaryPartRequestBoundaryType) => {
        setBoundaryTypes(previous => ({ ...previous, [id]: type }));
    }, []);

    const resolveType = (boundary: JohnDeereTreeBoundary): BoundaryPartRequestBoundaryType =>
        boundaryTypes[boundary.id] ?? DEFAULT_BOUNDARY_TYPE;

    const isImported = useCallback((id: string): boolean => importedIds.has(id), [importedIds]);

    const activeField = useMemo<JohnDeereTreeField | null>(() => {
        for (const client of tree) {
            for (const farm of client.farms ?? []) {
                for (const field of farm.fields ?? []) {
                    if (field.id === activeFieldId) {
                        return field;
                    }
                }
            }
        }

        return null;
    }, [tree, activeFieldId]);

    const mapBoundaries = activeField?.boundaries ?? [];
    const mapContainerRef = useBoundaryMap(mapBoundaries);

    const selectionCounts = useMemo(() => {
        let clients = 0;
        let farms = 0;
        let fields = 0;
        let boundaries = 0;
        let areaHa = 0;

        for (const client of tree) {
            if (selectedIds.has(client.id)) {
                clients += 1;
            }

            for (const farm of client.farms ?? []) {
                if (selectedIds.has(farm.id)) {
                    farms += 1;
                }

                for (const field of farm.fields ?? []) {
                    if (selectedIds.has(field.id)) {
                        fields += 1;
                        areaHa += computeFieldAreaHa(field);
                    }

                    for (const boundary of field.boundaries ?? []) {
                        if (selectedIds.has(boundary.id)) {
                            boundaries += 1;
                        }
                    }
                }
            }
        }

        return { areaHa, boundaries, clients, farms, fields };
    }, [tree, selectedIds]);

    const totalItems =
        selectionCounts.clients +
        selectionCounts.farms +
        selectionCounts.fields +
        selectionCounts.boundaries;

    const totalGrowers = tree.length;
    const totalFarms = tree.reduce((sum, c) => sum + (c.farms?.length ?? 0), 0);
    const totalFields = tree.reduce(
        (sum, c) => sum + (c.farms ?? []).reduce((sf, f) => sf + (f.fields?.length ?? 0), 0),
        0
    );
    const totalBoundaries = tree.reduce(
        (sum, c) =>
            sum +
            (c.farms ?? []).reduce(
                (sf, f) =>
                    sf +
                    (f.fields ?? []).reduce((sfd, fd) => sfd + (fd.boundaries?.length ?? 0), 0),
                0
            ),
        0
    );

    const handleClearSelection = () => {
        setSelectedIds(new Set());
        setBoundaryTypes({});
    };

    const handleConfirmImport = async () => {
        setIsReviewOpen(false);

        await importSelected({
            boundaryTypes,
            importedIds,
            jdToSvmIdMap,
            selectedIds,
            treeData: tree
        });

        await refreshImportedLookup();
        setSelectedIds(new Set());
        setBoundaryTypes({});
    };

    if (!isConnected) {
        return (
            <div className={styles.importPage} data-testid={testId}>
                <div className={styles.pageHeader}>
                    <div className={styles.pageTitleStack}>
                        <h1 className={styles.pageTitle}>Import from John Deere</h1>

                        <div className={styles.pageSubtitle}>
                            Connect your John Deere account on the Account page to continue.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.importPage} data-testid={testId}>
            <div className={styles.pageHeader}>
                <div className={styles.pageTitleStack}>
                    <h1 className={styles.pageTitle}>Import from John Deere</h1>

                    <div className={styles.pageSubtitle}>
                        {`${totalGrowers} growers · ${totalFarms} farms · ${totalFields} fields · ${totalBoundaries} boundaries available to import`}
                    </div>
                </div>

                <div className={styles.pageHeaderActions}>
                    <button
                        onClick={() => {
                            if (selectedOrgId) {
                                fetchTree({ organizationId: selectedOrgId });
                            }
                            refreshImportedLookup();

                            setLastSyncedAt(
                                new Date().toISOString().slice(0, 16).replace('T', ' ') + ' UTC'
                            );
                        }}
                        className={`${styles.btn} ${styles.btnGhost}`}
                        disabled={!selectedOrgId || isTreeLoading}
                        type='button'>
                        {isTreeLoading ? 'Syncing…' : 'Refresh'}
                    </button>
                </div>
            </div>

            <div className={styles.banner}>
                <span className={styles.connectionDot} />

                <div className={styles.bannerText}>
                    <span className={styles.bannerStrong}>
                        Connected to John Deere Operations Center
                    </span>

                    {selectedOrg ? (
                        <>
                            {' · Organization: '}

                            <span className={styles.bannerStrong}>{selectedOrg.name}</span>
                        </>
                    ) : null}

                    {lastSyncedAt ? (
                        <>
                            {' · last sync '}

                            <span className={styles.mono}>{lastSyncedAt}</span>
                        </>
                    ) : null}
                </div>

                {organizations.length > 1 && (
                    <select
                        className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
                        onChange={event => setSelectedOrgId(event.target.value)}
                        value={selectedOrgId ?? ''}>
                        {organizations.filter(isClientOrgEnabled).map(org => (
                            <option key={org.id} value={org.id}>
                                {org.name}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            <div className={styles.pageScroll}>
                <section className={styles.panel}>
                    <div className={styles.hierarchyHeader}>
                        <div className={styles.hphTitle}>Hierarchy</div>

                        <div className={styles.hphDivider} />

                        <div className={styles.hphSummary}>
                            <div className={styles.hphPrimary}>
                                {`${totalItems} items selected · ${selectionCounts.areaHa.toFixed(1)} ha`}
                            </div>

                            <div className={styles.hphSecondary}>
                                {`${selectionCounts.clients} growers · ${selectionCounts.farms} farms · ${selectionCounts.fields} fields · ${selectionCounts.boundaries} boundaries`}
                            </div>
                        </div>

                        <div className={styles.hphActions}>
                            <button
                                className={`${styles.btn} ${styles.btnGhost}`}
                                disabled={totalItems === 0 || isImporting}
                                onClick={handleClearSelection}
                                type='button'>
                                Clear
                            </button>

                            <button
                                className={`${styles.btn} ${styles.btnPrimary}`}
                                disabled={totalItems === 0 || isImporting}
                                onClick={() => setIsReviewOpen(true)}
                                type='button'>
                                {isImporting ? 'Importing…' : 'Review & Import'}
                            </button>
                        </div>
                    </div>

                    <div className={styles.panelBody}>
                        {isTreeLoading && tree.length === 0 && (
                            <div className={styles.pageSubtitle}>Loading John Deere data…</div>
                        )}

                        {!isTreeLoading && tree.length === 0 && (
                            <div className={styles.pageSubtitle}>
                                No growers found in this organization.
                            </div>
                        )}

                        {tree.map(client => {
                            const cOpen = openIds.has(client.id);
                            const cImported = isImported(client.id);
                            const cSel = selectedIds.has(client.id);
                            const farmCount = client.farms?.length ?? 0;
                            const fieldCount = (client.farms ?? []).reduce(
                                (sum, farm) => sum + (farm.fields?.length ?? 0),
                                0
                            );

                            return (
                                <div className={styles.clientGroup} key={client.id}>
                                    <div
                                        className={`${styles.clientRow} ${cOpen ? styles.open : ''}`}
                                        onClick={() => toggleOpen(client.id)}>
                                        <span
                                            onClick={event => {
                                                event.stopPropagation();

                                                if (!cImported) {
                                                    toggleSelected(client.id, 'client');
                                                }
                                            }}
                                            className={`${styles.rowCheckbox} ${cSel ? styles.selected : ''}`}
                                        />

                                        <span className={styles.cName}>{client.name}</span>

                                        <span className={styles.cMeta}>
                                            {`${farmCount} farms · ${fieldCount} fields`}
                                        </span>

                                        {cImported && (
                                            <span
                                                className={`${styles.statusPill} ${styles.statusOk}`}>
                                                <span className={styles.dot} />
                                                Imported
                                            </span>
                                        )}
                                    </div>

                                    {cOpen &&
                                        (client.farms ?? []).map(farm => {
                                            const fOpen = openIds.has(farm.id);
                                            const fImported = isImported(farm.id);
                                            const fSel = selectedIds.has(farm.id);
                                            const farmFieldCount = farm.fields?.length ?? 0;

                                            return (
                                                <div className={styles.farmGroup} key={farm.id}>
                                                    <div
                                                        className={`${styles.farmRow} ${fOpen ? styles.open : ''}`}
                                                        onClick={() => toggleOpen(farm.id)}>
                                                        <span
                                                            onClick={event => {
                                                                event.stopPropagation();

                                                                if (!fImported) {
                                                                    toggleSelected(farm.id, 'farm');
                                                                }
                                                            }}
                                                            className={`${styles.rowCheckbox} ${fSel ? styles.selected : ''}`}
                                                        />

                                                        <span className={styles.fName}>
                                                            {farm.name}
                                                        </span>

                                                        <span className={styles.fMeta}>
                                                            {farmFieldCount} fields
                                                        </span>

                                                        {fImported && (
                                                            <span
                                                                className={`${styles.statusPill} ${styles.statusOk}`}>
                                                                <span className={styles.dot} />
                                                                Imported
                                                            </span>
                                                        )}
                                                    </div>

                                                    {fOpen && farmFieldCount > 0 && (
                                                        <div className={styles.fieldList}>
                                                            {farm.fields.map(field => {
                                                                const fieldOpen = openIds.has(
                                                                    field.id
                                                                );
                                                                const fieldImp = isImported(
                                                                    field.id
                                                                );
                                                                const fieldSel = selectedIds.has(
                                                                    field.id
                                                                );
                                                                const bCount =
                                                                    field.boundaries?.length ?? 0;
                                                                const fAreaHa =
                                                                    computeFieldAreaHa(field);
                                                                const cardClass = [
                                                                    styles.fieldCard,
                                                                    fieldSel ? styles.selected : '',
                                                                    fieldImp ? styles.imported : ''
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(' ');

                                                                return (
                                                                    <div
                                                                        className={cardClass}
                                                                        key={field.id}>
                                                                        <div
                                                                            className={
                                                                                styles.fcTop
                                                                            }>
                                                                            <span
                                                                                onClick={event => {
                                                                                    event.stopPropagation();

                                                                                    setActiveFieldId(
                                                                                        field.id
                                                                                    );

                                                                                    if (!fieldImp) {
                                                                                        toggleSelected(
                                                                                            field.id,
                                                                                            'field'
                                                                                        );
                                                                                    }
                                                                                }}
                                                                                className={`${styles.rowCheckbox} ${fieldSel ? styles.selected : ''}`}
                                                                            />

                                                                            <div
                                                                                className={
                                                                                    styles.fcBody
                                                                                }>
                                                                                <div
                                                                                    className={
                                                                                        styles.fcTitleRow
                                                                                    }>
                                                                                    <div
                                                                                        className={
                                                                                            styles.fcName
                                                                                        }>
                                                                                        {field.name}
                                                                                    </div>

                                                                                    {fieldImp ? (
                                                                                        <span
                                                                                            className={`${styles.statusPill} ${styles.statusOk}`}>
                                                                                            <span
                                                                                                className={
                                                                                                    styles.dot
                                                                                                }
                                                                                            />
                                                                                            Imported
                                                                                        </span>
                                                                                    ) : (
                                                                                        <button
                                                                                            className={
                                                                                                styles.fcExpandToggle
                                                                                            }
                                                                                            onClick={event => {
                                                                                                event.stopPropagation();

                                                                                                toggleFieldOpen(
                                                                                                    field.id
                                                                                                );
                                                                                            }}
                                                                                            type='button'>
                                                                                            {fieldOpen
                                                                                                ? '▾ Hide'
                                                                                                : '▸ Show'}{' '}

                                                                                            {bCount}{' '}
                                                                                            boundaries
                                                                                        </button>
                                                                                    )}
                                                                                </div>

                                                                                <div
                                                                                    className={
                                                                                        styles.fcMeta
                                                                                    }>
                                                                                    <span>
                                                                                        <span
                                                                                            className={
                                                                                                styles.metaLabel
                                                                                            }>
                                                                                            Area
                                                                                        </span>

                                                                                        <span
                                                                                            className={
                                                                                                styles.metaValue
                                                                                            }>
                                                                                            {fAreaHa.toFixed(
                                                                                                1
                                                                                            )}{' '}
                                                                                            ha
                                                                                        </span>
                                                                                    </span>

                                                                                    <span>
                                                                                        <span
                                                                                            className={
                                                                                                styles.metaLabel
                                                                                            }>
                                                                                            Boundaries
                                                                                        </span>

                                                                                        <span
                                                                                            className={
                                                                                                styles.metaValue
                                                                                            }>
                                                                                            {bCount}
                                                                                        </span>
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {fieldOpen &&
                                                                            bCount > 0 && (
                                                                                <div
                                                                                    className={
                                                                                        styles.boundaryList
                                                                                    }>
                                                                                    {field.boundaries.map(
                                                                                        boundary => {
                                                                                            const bSel =
                                                                                                selectedIds.has(
                                                                                                    boundary.id
                                                                                                );
                                                                                            const bImp =
                                                                                                isImported(
                                                                                                    boundary.id
                                                                                                );
                                                                                            const currentType =
                                                                                                resolveType(
                                                                                                    boundary
                                                                                                );
                                                                                            const bArea =
                                                                                                computeBoundaryAreaHa(
                                                                                                    boundary
                                                                                                );

                                                                                            return (
                                                                                                <div
                                                                                                    key={
                                                                                                        boundary.id
                                                                                                    }
                                                                                                    className={`${styles.boundaryRow} ${bSel ? styles.selected : ''}`}>
                                                                                                    <span
                                                                                                        onClick={() => {
                                                                                                            setActiveFieldId(
                                                                                                                field.id
                                                                                                            );

                                                                                                            if (
                                                                                                                !bImp
                                                                                                            ) {
                                                                                                                toggleSelected(
                                                                                                                    boundary.id,
                                                                                                                    'boundary'
                                                                                                                );
                                                                                                            }
                                                                                                        }}
                                                                                                        className={`${styles.rowCheckbox} ${bSel ? styles.selected : ''}`}
                                                                                                    />

                                                                                                    <div
                                                                                                        className={
                                                                                                            styles.bName
                                                                                                        }>
                                                                                                        {
                                                                                                            boundary.name
                                                                                                        }
                                                                                                    </div>

                                                                                                    {bImp && (
                                                                                                        <span
                                                                                                            className={`${styles.statusPill} ${styles.statusOk}`}>
                                                                                                            <span
                                                                                                                className={
                                                                                                                    styles.dot
                                                                                                                }
                                                                                                            />
                                                                                                            Imported
                                                                                                        </span>
                                                                                                    )}

                                                                                                    <div
                                                                                                        className={
                                                                                                            styles.bArea
                                                                                                        }>
                                                                                                        {bArea.toFixed(
                                                                                                            1
                                                                                                        )}{' '}
                                                                                                        ha
                                                                                                    </div>

                                                                                                    <div
                                                                                                        className={
                                                                                                            styles.bTypeToggle
                                                                                                        }>
                                                                                                        {BOUNDARY_TYPE_OPTIONS.map(
                                                                                                            option => (
                                                                                                                <button
                                                                                                                    key={
                                                                                                                        option
                                                                                                                    }
                                                                                                                    onClick={() =>
                                                                                                                        setType(
                                                                                                                            boundary.id,
                                                                                                                            option
                                                                                                                        )
                                                                                                                    }
                                                                                                                    className={`${styles.bTypeOpt} ${option === currentType ? styles.active : ''}`}
                                                                                                                    type='button'>
                                                                                                                    {
                                                                                                                        TYPE_LABEL[
                                                                                                                            option
                                                                                                                        ]
                                                                                                                    }
                                                                                                                </button>
                                                                                                            )
                                                                                                        )}
                                                                                                    </div>
                                                                                                </div>
                                                                                            );
                                                                                        }
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            );
                        })}
                    </div>
                </section>

                <section className={styles.mapPanel}>
                    <div className={styles.mapPanelHeader}>
                        <div className={styles.mapPanelTitle}>Boundary Preview</div>

                        <span className={styles.mapPanelSubtitle}>
                            {activeField?.name ?? 'Select a field to preview'}
                        </span>
                    </div>

                    <div className={styles.mapContainer} ref={mapContainerRef} />
                </section>
            </div>

            {isReviewOpen && (
                <div className={styles.modalOverlay} onClick={() => setIsReviewOpen(false)}>
                    <div className={styles.modal} onClick={event => event.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <div className={styles.modalTitle}>Review import</div>

                            <button
                                className={styles.modalClose}
                                onClick={() => setIsReviewOpen(false)}
                                type='button'>
                                ×
                            </button>
                        </div>

                        <div className={styles.modalSubtitle}>
                            {'Importing '}

                            <span className={styles.modalStrong}>
                                {`${selectionCounts.clients} grower${selectionCounts.clients === 1 ? '' : 's'}`}
                            </span>

                            {', '}

                            <span className={styles.modalStrong}>
                                {`${selectionCounts.farms} farm${selectionCounts.farms === 1 ? '' : 's'}`}
                            </span>

                            {', '}

                            <span className={styles.modalStrong}>
                                {`${selectionCounts.fields} field${selectionCounts.fields === 1 ? '' : 's'}`}
                            </span>

                            {', '}

                            <span className={styles.modalStrong}>
                                {`${selectionCounts.boundaries} boundar${selectionCounts.boundaries === 1 ? 'y' : 'ies'}`}
                            </span>

                            {' — '}

                            <span className={styles.modalStrong}>
                                {`${selectionCounts.areaHa.toFixed(1)} ha total`}
                            </span>

                            {' into SVM'}
                        </div>

                        <div className={styles.modalBody}>
                            {tree.map(client => {
                                if (!selectedIds.has(client.id) || isImported(client.id)) {
                                    return null;
                                }
                                const farmCount = client.farms?.length ?? 0;
                                const fieldCount = (client.farms ?? []).reduce(
                                    (sum, farm) => sum + (farm.fields?.length ?? 0),
                                    0
                                );

                                return (
                                    <div
                                        className={`${styles.reviewNode} ${styles.reviewNodeClient}`}
                                        key={client.id}>
                                        <div className={styles.rnRow}>
                                            <div className={styles.rnName}>{client.name}</div>

                                            <div className={styles.rnMeta}>
                                                {`${farmCount} farms · ${fieldCount} fields`}
                                            </div>

                                            <button
                                                className={styles.rnRemove}
                                                onClick={() => toggleSelected(client.id, 'client')}
                                                type='button'>
                                                ×
                                            </button>
                                        </div>

                                        {(client.farms ?? []).map(farm => {
                                            if (!selectedIds.has(farm.id) || isImported(farm.id)) {
                                                return null;
                                            }
                                            const farmFieldCount = farm.fields?.length ?? 0;

                                            return (
                                                <div
                                                    className={`${styles.reviewNode} ${styles.reviewNodeFarm}`}
                                                    key={farm.id}>
                                                    <div className={styles.rnRow}>
                                                        <div className={styles.rnName}>
                                                            {farm.name}
                                                        </div>

                                                        <div className={styles.rnMeta}>
                                                            {`${farmFieldCount} fields`}
                                                        </div>

                                                        <button
                                                            onClick={() =>
                                                                toggleSelected(farm.id, 'farm')
                                                            }
                                                            className={styles.rnRemove}
                                                            type='button'>
                                                            ×
                                                        </button>
                                                    </div>

                                                    {(farm.fields ?? []).map(field => {
                                                        if (
                                                            !selectedIds.has(field.id) ||
                                                            isImported(field.id)
                                                        ) {
                                                            return null;
                                                        }
                                                        const bCount =
                                                            field.boundaries?.length ?? 0;
                                                        const fAreaHa = computeFieldAreaHa(field);

                                                        return (
                                                            <div
                                                                className={`${styles.reviewNode} ${styles.reviewNodeField}`}
                                                                key={field.id}>
                                                                <div className={styles.rnRow}>
                                                                    <div className={styles.rnName}>
                                                                        {field.name}
                                                                    </div>

                                                                    <div className={styles.rnMeta}>
                                                                        {`${fAreaHa.toFixed(1)} ha · ${bCount} boundaries`}
                                                                    </div>

                                                                    <button
                                                                        onClick={() =>
                                                                            toggleSelected(
                                                                                field.id,
                                                                                'field'
                                                                            )
                                                                        }
                                                                        className={styles.rnRemove}
                                                                        type='button'>
                                                                        ×
                                                                    </button>
                                                                </div>

                                                                {(field.boundaries ?? []).map(
                                                                    boundary => {
                                                                        if (
                                                                            !selectedIds.has(
                                                                                boundary.id
                                                                            ) ||
                                                                            isImported(boundary.id)
                                                                        ) {
                                                                            return null;
                                                                        }
                                                                        const bAreaHa =
                                                                            computeBoundaryAreaHa(
                                                                                boundary
                                                                            );
                                                                        const currentType =
                                                                            resolveType(boundary);
                                                                        const badgeClass =
                                                                            currentType === 'APPLY'
                                                                                ? styles.badgeApply
                                                                                : currentType ===
                                                                                    'NO_APPLY'
                                                                                  ? styles.badgeNoApply
                                                                                  : styles.badgeNoEntry;

                                                                        return (
                                                                            <div
                                                                                className={`${styles.reviewNode} ${styles.reviewNodeBoundary}`}
                                                                                key={boundary.id}>
                                                                                <div
                                                                                    className={
                                                                                        styles.rnRow
                                                                                    }>
                                                                                    <div
                                                                                        className={
                                                                                            styles.rnName
                                                                                        }>
                                                                                        {
                                                                                            boundary.name
                                                                                        }
                                                                                    </div>

                                                                                    <span
                                                                                        className={`${styles.badge} ${badgeClass}`}>
                                                                                        {
                                                                                            TYPE_LABEL[
                                                                                                currentType
                                                                                            ]
                                                                                        }
                                                                                    </span>

                                                                                    <div
                                                                                        className={
                                                                                            styles.rnMeta
                                                                                        }>
                                                                                        {`${bAreaHa.toFixed(1)} ha`}
                                                                                    </div>

                                                                                    <button
                                                                                        className={
                                                                                            styles.rnRemove
                                                                                        }
                                                                                        onClick={() =>
                                                                                            toggleSelected(
                                                                                                boundary.id,
                                                                                                'boundary'
                                                                                            )
                                                                                        }
                                                                                        type='button'>
                                                                                        ×
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>

                        <div className={styles.modalFooter}>
                            <button
                                className={`${styles.btn} ${styles.btnGhost}`}
                                disabled={isImporting}
                                onClick={() => setIsReviewOpen(false)}
                                type='button'>
                                Cancel
                            </button>

                            <button
                                className={`${styles.btn} ${styles.btnPrimary}`}
                                disabled={isImporting}
                                onClick={handleConfirmImport}
                                type='button'>
                                {isImporting ? 'Importing…' : `Import ${totalItems} items`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportPage;
